import React, { useState } from 'react'

import { gql, useMutation } from '@apollo/client'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material'

import Item from './Item'
import { client } from '../../../../../apollo'

const DELETE_USER = gql`
  mutation AdminDeleteUser($id: ID!) {
    adminDeleteUser(id: $id) {
      statusCode
      error
    }
  }
`

interface ActionsProps {
  user: {
    id: string
    email?: string | null
    name?: string | null
  }
  label: string
}

export default function Actions({ user, label }: ActionsProps) {
  const [openConfirm, setOpenConfirm] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const [deleteUser, { loading }] = useMutation(DELETE_USER, {
    client,
    onCompleted: (data) => {
      if (data.adminDeleteUser.statusCode === 200) {
        setOpenConfirm(false)
        window.location.reload()
      } else {
        setDeleteError(data.adminDeleteUser.error || 'Failed to delete user')
      }
    },
    onError: (error) => {
      setDeleteError(error.message || 'Failed to delete user')
    },
  })

  const handleConfirmOpen = () => {
    setOpenConfirm(true)
    setDeleteError(null)
  }

  const handleConfirmClose = () => {
    setOpenConfirm(false)
    setDeleteError(null)
  }

  const handleDeleteUser = async () => {
    try {
      await deleteUser({
        variables: {
          id: user.id,
        },
      })
    } catch (_error) {
      // TODO: handle error
    }
  }

  return (
    <>
      <Item label={label}>
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={handleConfirmOpen}
          disabled={loading}
        >
          Delete User
        </Button>
      </Item>

      {/* Confirmation Dialog */}
      <Dialog
        open={openConfirm}
        onClose={handleConfirmClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirm User Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this user?
            <br />
            <br />
            <strong>User ID:</strong> {user.id}
            <br />
            {user.email && (
              <>
                <strong>Email:</strong> {user.email}
                <br />
              </>
            )}
            {user.name && (
              <>
                <strong>Name:</strong> {user.name}
                <br />
              </>
            )}
            <br />
            <Typography color="error" variant="body2">
              ⚠️ This action cannot be undone!
            </Typography>
            {deleteError && (
              <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                Error: {deleteError}
              </Typography>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {/* eslint-disable-next-line jsx-a11y/no-autofocus -- Safe action should have focus in confirmation dialogs */}
          <Button onClick={handleConfirmClose} autoFocus>
            Cancel
          </Button>
          <Button onClick={handleDeleteUser} color="error" disabled={loading}>
            {loading ? 'Deleting...' : 'Delete User'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
